import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { z } from "zod";
import { cn } from "@/lib/utils.ts";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import { CommandLoading } from "cmdk";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { DatePickerWithPresets } from "@/components/ui/date-picker-with-presets.tsx";
import {
  CreateReferralData,
  ReferralType,
  formatReferralType,
  useCreateReferral,
  useSupervisedMentorships,
} from "@/lib/api/referrals.ts";
import { ReloadIcon } from "@radix-ui/react-icons";

const referralFormSchema = z.object({
  teenId: z.string().min(1, "Please select a teen"),
  referralDate: z.date({
    required_error: "Please select a date",
  }),
  referralRecipient: z.string().min(1, "Referral recipient is required"),
  referralType: z.nativeEnum(ReferralType, {
    required_error: "Please select a referral type",
  }),
  internalNote: z.string().optional(),
});

type ReferralFormData = z.infer<typeof referralFormSchema>;

const REFERRAL_TYPE_OPTIONS = Object.values(ReferralType).map((type) => ({
  label: formatReferralType(type),
  value: type,
}));

interface ReferralFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReferralForm({ open, onOpenChange }: ReferralFormProps) {
  const [teenSelectOpen, setTeenSelectOpen] = useState(false);
  const { mutateAsync: createReferral, isPending } = useCreateReferral();
  const { data: supervisedData, isPending: isMentorshipsLoading } =
    useSupervisedMentorships(true); // Include deactivated teens for referrals

  const form = useForm<ReferralFormData>({
    resolver: zodResolver(referralFormSchema),
    defaultValues: {
      referralDate: new Date(),
    },
  });

  // Get available teens from supervised mentorships (including deactivated for referrals)
  const availableTeens = useMemo(() => {
    if (!supervisedData?.mentorships) return [];
    return supervisedData.mentorships.map((mentorship) => ({
      id: mentorship.mentee_id,
      name: mentorship.mentee_name,
      isDeactivated: mentorship.mentorship_deactivated,
    }));
  }, [supervisedData]);

  // Get selected teen name
  const selectedTeenName = useMemo(() => {
    const selectedTeenId = form.watch("teenId");
    if (!selectedTeenId) return undefined;
    return availableTeens.find((teen) => teen.id === selectedTeenId)?.name;
  }, [form.watch("teenId"), availableTeens]);

  async function onSubmit(data: ReferralFormData) {
    try {
      const referralData: CreateReferralData = {
        teenId: data.teenId,
        referralDate: format(data.referralDate, "yyyy-MM-dd"),
        referralRecipient: data.referralRecipient,
        referralType: data.referralType,
        internalNote: data.internalNote || undefined,
      };

      await createReferral(referralData);

      // Reset form and close modal
      form.reset({
        referralDate: new Date(),
      });
      onOpenChange(false);
    } catch (error) {
      // Error is handled by the mutation hook
      console.error("Failed to create referral:", error);
    }
  }

  const handleCancel = () => {
    form.reset({
      referralDate: new Date(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Log referral</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Teen Selection - Searchable */}
            <FormField
              control={form.control}
              name="teenId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Teen name <span className="text-red-500">*</span>
                  </FormLabel>
                  <Popover
                    open={teenSelectOpen}
                    onOpenChange={setTeenSelectOpen}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={teenSelectOpen}
                          className={cn(
                            "w-full justify-between",
                            !field.value && "text-muted-foreground",
                          )}
                          disabled={isMentorshipsLoading}
                        >
                          {field.value ? selectedTeenName : "Select a teen"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput placeholder="Search teen..." />
                        <CommandList>
                          {isMentorshipsLoading ? (
                            <CommandLoading>Loading teens...</CommandLoading>
                          ) : (
                            <>
                              <CommandEmpty>No teens found.</CommandEmpty>
                              <CommandGroup>
                                {availableTeens.map((teen) => (
                                  <CommandItem
                                    key={teen.id}
                                    value={teen.name}
                                    onSelect={() => {
                                      field.onChange(teen.id);
                                      setTeenSelectOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === teen.id
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                    {teen.name}
                                    {teen.isDeactivated && (
                                      <span className="text-muted-foreground ml-2 text-xs">
                                        (inactive)
                                      </span>
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date Selection */}
            <FormField
              control={form.control}
              name="referralDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Date <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <DatePickerWithPresets
                      date={field.value}
                      onDateChange={(date) => field.onChange(date)}
                      className="w-full"
                      disablePastDates={false}
                      maxDate={new Date()}
                      showPresets={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Referral Recipient */}
            <FormField
              control={form.control}
              name="referralRecipient"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Referral recipient <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter referral recipient" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Referral Type */}
            <FormField
              control={form.control}
              name="referralType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Type for referral <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select referral type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REFERRAL_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Internal Notes */}
            <FormField
              control={form.control}
              name="internalNote"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add additional notes about this referral"
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                className="bg-black text-white hover:bg-gray-800"
              >
                {isPending && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
